import type { ReactNode } from "react";
import { Tabs, type TabConfig } from "@/components/ui";
import { NavLink } from "react-router";
import { cn } from "@/lib/utils";

export type DetailTabConfig<T extends string = string> = TabConfig<T> & {
  description: string;
  icon: ReactNode;
};

interface DetailTabNavProps<T extends string> {
  tabs: DetailTabConfig<T>[];
  activeTab: T;
  onTabChange: (tab: T) => void;
  ariaLabel: string;
  idPrefix: string;
  layout?: "equal" | "wrap";
}

export function DetailTabNav<T extends string>({
  tabs,
  activeTab,
  onTabChange,
  ariaLabel,
  idPrefix,
  layout = "equal",
}: DetailTabNavProps<T>) {
  return (
    <Tabs
      variant="panel"
      layout={layout}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={onTabChange}
      ariaLabel={ariaLabel}
      idPrefix={idPrefix}
      renderTabLink={({
        to,
        className,
        children,
        id,
        "aria-selected": ariaSelected,
        "aria-controls": ariaControls,
      }) => (
        <NavLink
          to={to}
          role="tab"
          id={id}
          aria-selected={ariaSelected}
          aria-controls={ariaControls}
          className={className}
        >
          {children}
        </NavLink>
      )}
    />
  );
}

interface DetailTabPanelProps {
  id: string;
  labelledBy: string;
  isActive: boolean;
  children: ReactNode;
  className?: string;
}

export function DetailTabPanel({
  id,
  labelledBy,
  isActive,
  children,
  className,
}: DetailTabPanelProps) {
  return (
    <div
      role="tabpanel"
      id={id}
      aria-labelledby={labelledBy}
      hidden={!isActive}
      className={cn("w-full min-w-0", !isActive && "hidden", className)}
    >
      {children}
    </div>
  );
}

interface DetailTabPanelHeadingProps {
  title: string;
  hint: string;
}

export function DetailTabPanelHeading({ title, hint }: DetailTabPanelHeadingProps) {
  return (
    <div className="mb-5">
      <h2 className="text-base font-semibold tracking-tight text-surface-900">{title}</h2>
      <p className="mt-1 text-sm text-surface-500">{hint}</p>
    </div>
  );
}
